import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { esquemaSenha } from '@/features/auth/senha';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { CriteriosSenha } from '@/components/criterios-senha';

const esquema = z
  .object({
    novaSenha: esquemaSenha,
    confirmacao: z.string().min(1, 'Repita a nova senha.'),
  })
  .refine((dados) => dados.novaSenha === dados.confirmacao, {
    message: 'As senhas não conferem.',
    path: ['confirmacao'],
  });

type Formulario = z.infer<typeof esquema>;

export function PaginaRedefinirSenha() {
  const [parametros] = useSearchParams();
  const token = parametros.get('token');
  const navegar = useNavigate();
  const [erro, setErro] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { novaSenha: '', confirmacao: '' },
  });

  const senhaDigitada = watch('novaSenha');

  const aoEnviar = handleSubmit(async (dados) => {
    if (!token) return;
    setErro(null);

    try {
      await api.post('/auth/reset-password', { token, novaSenha: dados.novaSenha });
      toast.success('Senha redefinida. Entre com a nova senha.');
      void navegar('/login', { replace: true });
    } catch (e) {
      setErro(
        e instanceof ErroApi ? e.mensagemUsuario : 'Não foi possível concluir. Tente novamente.',
      );
    }
  });

  return (
    <main className="bg-fundo flex min-h-dvh items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="bg-marca text-marca-contraste flex size-14 items-center justify-center rounded-2xl">
            <Stethoscope className="size-7" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">KAVI</h1>
        </div>

        <div className="bg-cartao rounded-xl border p-6 shadow-sm">
          {!token ? (
            <div className="flex flex-col gap-3">
              <AlertCircle className="text-destrutivo size-8" aria-hidden="true" />
              <h2 className="text-lg font-semibold">Link inválido</h2>
              <p className="text-silenciado-texto text-sm">
                O endereço acessado não contém um token de redefinição. Solicite um novo link.
              </p>
              <Botao asChild>
                <Link to="/esqueci-senha">Solicitar novo link</Link>
              </Botao>
            </div>
          ) : (
            <form
              onSubmit={(evento) => void aoEnviar(evento)}
              noValidate
              className="flex flex-col gap-4"
            >
              <div>
                <h2 className="text-lg font-semibold">Criar nova senha</h2>
                <p className="text-silenciado-texto text-sm">
                  Escolha uma senha que você não use em outros sistemas.
                </p>
              </div>

              {erro && (
                <div
                  role="alert"
                  className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo flex items-start gap-2 rounded-md border p-3 text-sm"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{erro}</span>
                </div>
              )}

              <Campo
                id="novaSenha"
                rotulo="Nova senha"
                obrigatorio
                erro={errors.novaSenha?.message}
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  {...register('novaSenha')}
                />
              </Campo>

              <CriteriosSenha senha={senhaDigitada} />

              <Campo
                id="confirmacao"
                rotulo="Repita a nova senha"
                obrigatorio
                erro={errors.confirmacao?.message}
              >
                <Entrada type="password" autoComplete="new-password" {...register('confirmacao')} />
              </Campo>

              <Botao type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
                {isSubmitting ? 'Salvando…' : 'Salvar nova senha'}
              </Botao>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
